import type react from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicParagraph } from '../../foundations/typefaces/BasicParagraph';
import { SmallParagraph } from '../../foundations/typefaces/SmallParagraph';
import style from '../../style';
import type { Color, Size as ThemeSize } from '../../style/types/theme';
import type Theme from '../../style/types/theme';
import { Size, Status } from './types';

export interface StyledInputProps {
  $status: Status;
  disabled?: boolean;
  readOnly?: boolean;
  $isScrolling?: boolean;
  $hasEllipsis: boolean;
  $hasIcon?: boolean;
  $forceFontSize?: string;
}

interface StyledSmallParagraphProps {
  $status?: Status;
  $disabled?: boolean;
  $visible?: boolean;
}

interface AnimationProps {
  theme: Theme;
}

interface WrapperProps {
  $width?: string;
}

export interface InputWrapperProps {
  $status: Status;
  $disabled?: boolean;
  $size: Size;
  $hasIcon?: boolean;
}

interface RightIconWrapperProps {
  $size: Size;
  $status: Status;
  $isClickable: boolean;
  $disabled: boolean;
}

const getHeightInput = (size: Size, themeSize: ThemeSize) => {
  switch (size) {
    case Size.SMALL:
      return themeSize.units(4);
    case Size.BIG:
      return themeSize.units(6);
    case Size.MEDIUM:
    default:
      return themeSize.units(5);
  }
};

export const getInputColors = (status: Status, themeColor: Color) => {
  const inputColors = {
    [Status.SUCCESS]: {
      borderColor: themeColor.border.success.contrast(),
      hoverBorderColor: themeColor.border.success.contrast(),
      placeHolderColor: themeColor.content.success.contrast(),
      textColor: themeColor.content.success.contrast(),
    },
    [Status.ERROR]: {
      borderColor: themeColor.border.error.default(),
      hoverBorderColor: themeColor.border.error.default(),
      placeHolderColor: themeColor.content.error.default(),
      textColor: themeColor.content.error.default(),
    },
    [Status.DEFAULT]: {
      borderColor: themeColor.border.primary.default(),
      hoverBorderColor: themeColor.border.primary.hover(),
      placeHolderColor: themeColor.content.secondary.default(0.55),
      textColor: themeColor.content.secondary.default(),
    },
  };

  return inputColors[status] || inputColors[Status.DEFAULT];
};

const getHoverIconColor = (status: Status, color: Color, disabled: boolean) => {
  if (disabled) {
    return color.content.reversed.hover();
  }

  return status === Status.DEFAULT
    ? color.content.primary.hover()
    : getInputColors(status, color).textColor;
};

const getIconCursor = ($disabled: boolean, $isClickable: boolean) => {
  if ($disabled) {
    return 'not-allowed';
  }

  return $isClickable ? 'pointer' : 'default';
};

const getSidesPadding = (size: Size, themeSize: ThemeSize) => {
  switch (size) {
    case Size.SMALL:
      return themeSize.units(1);
    case Size.BIG:
      return themeSize.units(2);
    case Size.MEDIUM:
    default:
      return themeSize.units(1.5);
  }
};

const inAnimation = ({ theme: { size } }: AnimationProps) =>
  keyframes({
    '0%': {
      opacity: 0,
      transform: `translateY(${size.units(-1)})`,
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0px)',
    },
  });

const outAnimation = ({ theme: { size } }: AnimationProps) =>
  keyframes({
    '0%': {
      opacity: 1,
      transform: 'translateY(0px)',
    },
    '100%': {
      opacity: 0,
      transform: `translateY(${size.units(-1)})`,
    },
  });

const withAnimation = (Component: react.ElementType) =>
  styled(Component)<{ $active: boolean }>(
    ({ $visible, $hasAnimation }) => css`
      animation: ${$visible ? inAnimation : outAnimation} ${$hasAnimation ? 0.2 : 0}s
        cubic-bezier(0.4, 0, 0.4, 1) forwards;
    `,
  );

export const StyledSmallParagraph = withAnimation(
  styled(SmallParagraph)<StyledSmallParagraphProps>(
    ({ theme: { color, size }, $status = Status.DEFAULT, $disabled = false }) => ({
      color: $disabled
        ? color.content.secondary.disabled()
        : getInputColors($status, color).textColor,
      paddingTop: size.units(0.25),
      pointerEvents: 'none',
    }),
  ),
);

export const RightIconWrapper = styled.div<RightIconWrapperProps>(
  ({ theme: { size, color, transition }, $size, $status, $isClickable, $disabled }) => ({
    cursor: getIconCursor($disabled, $isClickable),
    paddingRight: getSidesPadding($size, size),
    paddingLeft: size.units(1),
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    color: $disabled
      ? color.content.secondary.disabled()
      : getInputColors($status, color).textColor,
    transition: transition.standard('color'),

    ...style.mixins.devices.desktop({
      '&:hover': {
        color: getHoverIconColor($status, color, $disabled),
      },
    }),
  }),
);

export const InputWrapper = styled.div<InputWrapperProps>(
  ({ theme: { size, color, transition }, $disabled, $status, $size, $hasIcon }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: $disabled
      ? color.background.primary.disabled()
      : color.background.primary.default(),
    height: getHeightInput($size, size),
    border: `1px solid ${
      $disabled
        ? color.border.primary.default()
        : getInputColors($status, color).borderColor
    }`,
    borderRadius: $size === Size.SMALL ? size.units(0.5) : size.units(1),
    cursor: $disabled ? 'not-allowed' : undefined,
    transition: transition.standard('border'),
    padding: `0 ${$hasIcon ? 0 : getSidesPadding($size, size)} 0 ${getSidesPadding(
      $size,
      size,
    )}`,

    '&:focus-within': {
      borderColor:
        $status === Status.DEFAULT
          ? color.border.accent.default()
          : getInputColors($status, color).borderColor,
    },

    ...style.mixins.devices.desktop({
      ':hover:not(:focus-within)': {
        borderColor: $disabled
          ? undefined
          : getInputColors($status, color).hoverBorderColor,
      },
    }),
  }),
);

export const StyledInput = styled(BasicParagraph)<StyledInputProps>(
  ({
    theme: { color, transition },
    $status,
    readOnly,
    disabled,
    $isScrolling,
    $hasEllipsis,
    $forceFontSize,
  }) => ({
    width: '100%',
    background: 'none',
    color: disabled
      ? color.content.secondary.disabled()
      : getInputColors($status, color).textColor,
    transition: transition.standard('color'),
    outline: 'none',
    border: 'none',
    padding: 0,
    cursor: disabled ? 'not-allowed' : undefined,

    ...style.mixins.ellipsis($hasEllipsis && !$isScrolling),

    ...(readOnly && {
      caretColor: 'transparent',
    }),

    '::placeholder': {
      color: disabled
        ? color.content.primary.disabled()
        : getInputColors($status, color).placeHolderColor,
    },

    // The following styles are needed to fix iOS Safari disabled state
    // https://stackoverflow.com/questions/262158/disabled-input-text-color-on-ios
    ':disabled, :disabled::placeholder': {
      opacity: 1,
      '-webkit-text-fill-color': color.content.primary.disabled(),
    },
    fontSize: $forceFontSize,
  }),
);

export const StyledTextarea = styled(StyledInput)<StyledInputProps>(({ theme }) => ({
  minHeight: theme.size.units(12),
  resize: 'none',
  padding: `0 ${theme.size.units(1)} 0 0`,
  border: 'none',
  borderRadius: 0,

  ...style.mixins.scrollbar(theme),
}));

export const Wrapper = styled.div<WrapperProps>(({ $width }) => ({
  width: $width || '100%',
}));
